import { useEffect } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { InternshipDetailsList } from '#/components/DetailsList/Internship/InternshipDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewIdealistAtsUserApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewIdealistAtsUserApplicationForm';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { ListingViewSidebarContent } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarContent';
import { ListingViewSidebarImage } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarImage';
import { ListingViewSidebarLinks } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarLinks';
import { ListingViewSidebarTitle } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebarTitle';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewBenefitsSection } from '#/modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { trackViewListingAtsSection } from '#/modules/listing/helpers/trackViewListingAtsSection';
import { useListingApplicationSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmission';
import { useListingApplicationTemporaryStorageSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationTemporaryStorageSubmission';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import type { ApiInternship } from '#/modules/listing/internships/api/types/ApiInternship';
import { ViewInternshipActionButtons } from '#/modules/listing/internships/components/ActionButtons/ViewInternshipActionButtons';
import { getInternshipApplySectionTitle } from '#/modules/listing/internships/helpers/getInternshipApplySectionTitle';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { legacyIsExternalURL } from '#/utils/url/isExternalURL';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  internship: ApiInternship;
  listingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
};

export function ViewInternshipPage({
  internship,
  listingApplicationId,
  initiallySaved,
  orgInitiallySaved,
}: Props) {
  const { i18n, locale } = useI18n();

  const {
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    id,
    hasAts,
    benefits,
    benefitsTruncated,
    startDate,
    endDate,
    functions,
    requestedAttachments,
    requestedAttachmentsHash,
    firstPublished,
    isPostedAnonymously,
    name,
    type,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    wage,
    org,
    orgNameOverride,
    orgLogoOverride,
    payment,
    hoursPerWeek,
    academicCredits,
    applicationDeadline,
  } = internship;

  const [applied, setApplied] = useKeyedState(Boolean(listingApplicationId), {
    key: internship.id,
  });

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts,
      listingType: type,
      listingId: id,
      listingName: name,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType, type]);

  const orgId = org?.id ?? null;
  const orgType = org?.orgType ?? null;
  const orgLogo = orgLogoOverride ?? org?.logo ?? null;
  const orgName = orgNameOverride ?? org?.name ?? null;
  const orgUrl = org?.url[locale] ?? null;

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: internship,
    orgInitiallySaved,
    initiallySaved,
    searchSavedListingsStore: null,
  });

  const { listingApplicationInitialValues, submitListingApplication } =
    useListingApplicationSubmission({
      listingId: id,
      listingType: type,
      applied,
      onApplied: () => setApplied(true),
      orgId: org?.id ?? null,
      orgType: org?.orgType ?? null,
    });

  const { formRef, temporaryStorageInitialValues } =
    useListingApplicationTemporaryStorageSubmission();

  return (
    <>
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this internship')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="INTERNSHIP"
              orgId={org.id}
              hasAts={hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ListingViewSidebar maxWidth={288}>
          <ListingViewSidebarImage
            orgLogo={orgLogo}
            orgName={orgName}
            orgURLForCurrentLocale={orgUrl}
          />
          <ListingViewSidebarTitle
            address={address}
            listingName={name}
            listingType="INTERNSHIP"
            orgName={orgName}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
          />
          <ListingViewSidebarContent
            firstPublished={firstPublished}
            listingType={type}
            showScrollToApplyButton={hasAts}
            applied={applied}
            listingViewStatus={ListingViewStatus.PUBLISHED}
          />
          <ListingViewSidebarLinks
            searchContext={searchContext}
            orgUrl={orgUrl}
          />
        </ListingViewSidebar>

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={org?.orgType ?? null}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
            listingViewStatus={ListingViewStatus.PUBLISHED}
            actionButtons={
              <ViewInternshipActionButtons
                internship={internship}
                applied={applied}
                internshipSaved={saved}
                onSaveInternship={save}
                onUnsaveInternship={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <InternshipDetailsList
              academicCredits={academicCredits}
              startDate={startDate}
              endDate={endDate}
              applicationDeadline={applicationDeadline}
              payment={payment}
              wage={wage}
              hoursPerWeek={hoursPerWeek}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={null}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && legacyIsExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'INTERNSHIP',
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this internship')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: internship.type,
                locale: internship.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={benefits}
            benefitsTruncated={benefitsTruncated}
          />

          <ListingViewLocationSection
            listingType="INTERNSHIP"
            address={address}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            title={getInternshipApplySectionTitle({ i18n, hasAts })}
          >
            {hasAts ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>
                {applied ? (
                  <ListingViewAppliedMessage />
                ) : (
                  <ListingViewApplyAts
                    applyText={applyText}
                    applicationForm={
                      <ListingViewIdealistAtsUserApplicationForm
                        listingId={id}
                        listingType="INTERNSHIP"
                        requestedAttachments={requestedAttachments}
                        submission={{
                          onSubmit: submitListingApplication,
                          listingApplicationInitialValues,
                          requestedAttachmentsHash,
                          temporaryStorage: {
                            formRef,
                            initialValues: temporaryStorageInitialValues,
                          },
                        }}
                      />
                    }
                    onReach={() =>
                      trackViewListingAtsSection({
                        listingId: id,
                        listingType: 'INTERNSHIP',
                        orgId,
                        orgType,
                        applied,
                      })
                    }
                  />
                )}
              </>
            ) : (
              <ListingViewApplyExternal
                applyEmail={applyEmail}
                applyText={applyText}
                applyTextTruncated={applyTextTruncated}
                applyUrl={applyUrl}
                applied={applied}
                onMarkAsApplied={() => Promise.resolve()}
                functions={functions}
                listingName={name}
                listingType="INTERNSHIP"
                listingId={id}
                orgId={orgId}
                orgType={orgType}
                synonym={null}
                isPreview={false}
              />
            )}
          </ListingViewApplySection>

          {searchContext && (
            <PrintHidden>
              <Box mt={70} mb={48}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </Box>
            </PrintHidden>
          )}
        </ListingViewMain>
      </ListingViewBody>

      {!user && (
        <PrintHidden>
          <ListingViewFooter>
            <ListingViewSignupCta listingUrl={url[locale]} type={type} />
          </ListingViewFooter>
        </PrintHidden>
      )}
    </>
  );
}
